import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {CurrentUserRoleAndLogin, JwtTokens, roleDescriptions, UserRole} from '../types/auth.types';
import {JWT_ACCESS_TOKEN, JWT_REFRESH_TOKEN} from '../constants/constants';
import {jwtDecode} from 'jwt-decode';
import {defaultCurrentUserRoleAndLogin} from "../models/default-current-user-role-and-login";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private loggedUsername: string | null = null;

  private userRoles$: BehaviorSubject<Array<UserRole>> = new BehaviorSubject<Array<UserRole>>([]);
  private isAuthenticatedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public logout(): void {
    this.deleteTokens();
    this.setIsAuthenticated(false);
    this.loggedUsername = null;
  }

  public isLoggedIn(): boolean {
    return !!localStorage.getItem(JWT_ACCESS_TOKEN);
  }

  public doLoginUser(username: string, tokens: JwtTokens): void {
    this.loggedUsername = username;
    this.storeJwtTokens(tokens);
    this.setIsAuthenticated(true);
  }

  public storeJwtTokens(tokens: JwtTokens): void {
    this.storeJwtToken(JWT_ACCESS_TOKEN, tokens.access);
    this.storeJwtToken(JWT_REFRESH_TOKEN, tokens.refresh);
    const userRoles = this.getRoleFromAccessToken(tokens.access);
    this.setUserRoles(userRoles);
  }

  public getRolesAndLogin(): CurrentUserRoleAndLogin {
    const token = localStorage.getItem(JWT_ACCESS_TOKEN);
    if (!token) { return defaultCurrentUserRoleAndLogin; }

    const userRoles = this.getRoleFromAccessToken(token);
    const userRole = roleDescriptions[userRoles[0]];
    const userLogin = this.getLoginFromAccessToken(token);

    return {
      displayRole: userRole,
      role: userRoles[0],
      login: userLogin
    };
  }

  public getUserRoles(): Array<UserRole> {
    return this.userRoles$.value;
  }

  public getJwtTokens(): JwtTokens {
    return {
      access: localStorage.getItem(JWT_ACCESS_TOKEN)!,
      refresh: localStorage.getItem(JWT_REFRESH_TOKEN)!
    }
  }

  private setUserRoles(userRoles: Array<UserRole>): void {
    this.userRoles$.next(userRoles);
  }

  private setIsAuthenticated(isAuthenticated: boolean): void {
    this.isAuthenticatedSubject.next(isAuthenticated);
  }

  private storeJwtToken(tokenKey: string, tokenValue: string): void {
    localStorage.setItem(tokenKey, tokenValue);
  }

  private deleteTokens(): void {
    localStorage.removeItem(JWT_ACCESS_TOKEN);
    localStorage.removeItem(JWT_REFRESH_TOKEN);
  }

  private getRoleFromAccessToken(accessToken: string): Array<UserRole> {
    const jwtData: { roles: Array<UserRole> } = jwtDecode(accessToken);
    return jwtData.roles;
  }

  private getLoginFromAccessToken(accessToken: string): string {
    const jwtData: { sub: string } = jwtDecode(accessToken);
    return jwtData.sub;
  }
}
